import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import {
  LimitPoint,
  RetroChart as RetroChartModel,
} from "../../../models/chartData";
import {
  buildRetroChartOptions,
  RetroLimitKey,
} from "../options/buildRetroChartOptions";

interface RetroChartProps {
  title: string;
  chart: RetroChartModel;
  limits?: LimitPoint[];
  limitKey?: RetroLimitKey;
  activeSeries?: Record<string, boolean>;
  colors?: Record<string, string>;
  onToggleSeries?: (seriesName: string) => void;
  onDataZoom?: (params: any) => void;
  onCursorMove?: (time: number | null) => void;
  minDate?: number;
  maxDate?: number;
  zoomStart?: number;
  zoomEnd?: number;
  dynamicLevel?: number;
  headerExtra?: React.ReactNode;
}

export const RetroChart: React.FC<RetroChartProps> = ({
  title,
  chart,
  limits,
  limitKey,
  activeSeries,
  colors,
  onToggleSeries,
  onDataZoom,
  onCursorMove,
  minDate,
  maxDate,
  zoomStart,
  zoomEnd,
  dynamicLevel,
  headerExtra,
}) => {
  const options = useMemo(
    () =>
      buildRetroChartOptions({
        chart,
        limits,
        limitKey,
        activeSeries,
        colors,
        minDate,
        maxDate,
        zoomStart,
        zoomEnd,
        dynamicLevel,
      }),
    [
      chart,
      limits,
      limitKey,
      activeSeries,
      colors,
      minDate,
      maxDate,
      zoomStart,
      zoomEnd,
      dynamicLevel,
    ],
  );

  const onEvents = useMemo(
    () => ({
      legendselectchanged: (params: any) => {
        const { name, selected } = params;
        if (onToggleSeries && selected[name] !== undefined) {
          onToggleSeries(name);
        }
      },
      updateAxisPointer: (params: any) => {
        if (!onCursorMove) return;
        const xValue = params.axesInfo?.[0]?.value ?? null;
        onCursorMove(xValue);
      },
      dataZoom: onDataZoom,
    }),
    [onToggleSeries, onCursorMove, onDataZoom],
  );

  return (
    <div className="chart-section">
      <div className="chart-title">
        {title}
        {headerExtra ? <span className="chart-title-extra">{headerExtra}</span> : null}
      </div>
      <ReactECharts
        option={options}
        style={{ height: 300, width: "100%" }}
        onEvents={onEvents}
        opts={{ renderer: "canvas" }}
        notMerge={false}
        lazyUpdate={true}
      />
    </div>
  );
};

export default RetroChart;
